import React, { useEffect, useRef, useState } from "react";
import { Paper } from "@material-ui/core";
import { useAlert } from "react-alert";
import { Spinner, Table } from "react-bootstrap";
import ReactToPrint from "react-to-print";
import { DatePicker } from "antd";
import { upperCase } from "lodash";
import { useSelector } from "react-redux";
import { DownloadTableExcel } from "react-export-table-to-excel";
import { baseUrl } from "../../../../services/config";
const { RangePicker } = DatePicker;

const pageStyle = `
  @page {
    size: landscape !important;
}
`;

const VIEWS = [
  { key: "summary", label: "Class / Section / Subject" },
  { key: "uploaders", label: "Uploaded By" },
  { key: "details", label: "All Home Work" },
];

const NOT_RECORDED = "Not recorded";

const HomeWorkAnalysis = () => {
  const user = useSelector((state) => state.user.user);
  const alert = useAlert();
  let authtoken = localStorage.getItem("userToken");

  const [allSessions, setAllSessions] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [campusData] = useState(user.campuses || []);
  const [campusId, setCampusId] = useState("");
  const [classesData, setClassesData] = useState([]);
  const [classId, setClassId] = useState("");
  const [educationType, setEducationType] = useState("1");
  const [sectionData, setSectionData] = useState([]);
  const [sectionId, setSectionId] = useState("");
  const [subjectsData, setSubjectsData] = useState([]);
  const [subjectId, setSubjectId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [report, setReport] = useState(null);
  const [view, setView] = useState("summary");
  const [reportTitle, setReportTitle] = useState("");

  const [unitName, setUnitName] = useState("");
  const [icon, setIcon] = useState("");
  const [printLoading, setPrintLoading] = useState(false);
  const componentRef = useRef(null);
  // State rather than a ref so the Export button re-renders once the table exists.
  const [tableElement, setTableElement] = useState(null);

  useEffect(() => {
    getSettingData();
    getAllSessions();
    getSubjects();
  }, []);

  const authGet = (url) =>
    fetch(`${baseUrl}/${url}`, {
      method: "GET",
      headers: { Authorization: `Bearer ${authtoken}` },
    }).then((res) => res.json());

  const getSettingData = () => {
    authGet("get-settings")
      .then((data) => {
        setUnitName(upperCase(data.payload.settings.unit_name));
        setIcon(data.payload.settings.logo_file);
      })
      .catch((err) => console.log(err));
  };

  const getAllSessions = () => {
    authGet("session")
      .then((data) => {
        setAllSessions(data.payload || []);
        (data.payload || []).forEach((session) => {
          if (session.active_academic_year == "1") {
            setSessionId(session.id);
          }
        });
      })
      .catch((err) => console.log(err));
  };

  const getSubjects = () => {
    authGet("subject")
      .then((data) => setSubjectsData(data.payload || []))
      .catch((err) => console.log(err));
  };

  const getSections = (campus, type, studentClass) => {
    setSectionId("");
    setSectionData([]);
    if (!campus || !studentClass) return;
    authGet(`class_section/${campus}/${type}/${studentClass}`)
      .then((data) => setSectionData(data.payload || []))
      .catch((err) => console.log(err));
  };

  const onCampusChange = (id) => {
    setCampusId(id);
    setClassId("");
    setClassesData([]);
    setSectionId("");
    setSectionData([]);
    if (!id) return;
    authGet(`campusclass/${id}`)
      .then((data) => setClassesData(data.payload || []))
      .catch((err) => console.log(err));
  };

  const onClassChange = (id) => {
    setClassId(id);
    getSections(campusId, educationType, id);
  };

  const onEducationTypeChange = (type) => {
    setEducationType(type);
    getSections(campusId, type, classId);
  };

  const onDateChange = (_, dates) => {
    setStartDate(dates && dates[0] ? dates[0] : "");
    setEndDate(dates && dates[1] ? dates[1] : "");
  };

  // Validation errors arrive as { field: [messages] }; anything else as a string.
  const getErrorMessage = (data) => {
    const message = data?.metadata?.message ?? data?.message;
    if (message && typeof message === "object") {
      const first = Object.values(message)[0];
      return Array.isArray(first) ? first[0] : String(first);
    }
    return message || "Something went wrong, please try again";
  };

  const handleSearch = () => {
    if (!sessionId) return alert.error("Session is required");
    if (!campusId) return alert.error("Campus is required");

    const nameOf = (list, id) => list.find((item) => item.id == id)?.name;
    const parts = [
      nameOf(campusData, campusId),
      nameOf(classesData, classId),
      nameOf(sectionData, sectionId) && `(${nameOf(sectionData, sectionId)})`,
      nameOf(subjectsData, subjectId) && `- ${nameOf(subjectsData, subjectId)}`,
    ].filter(Boolean);
    setReportTitle(
      `Home Work Analysis of ${parts.join(" ")}${
        startDate ? ` From ${startDate} To ${endDate}` : ""
      }`
    );

    setLoading(true);
    fetch(`${baseUrl}/home-work-analysis`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${authtoken}`,
      },
      body: JSON.stringify({
        year_id: sessionId,
        campus_id: campusId,
        student_class_id: classId || null,
        global_section_id: sectionId || null,
        subject_id: subjectId || null,
        education_type: educationType,
        start_date: startDate || null,
        end_date: endDate || null,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        setLoading(false);
        setSearched(true);
        if (data?.metadata?.success) {
          setReport(data.payload);
          if (!data.payload?.details?.length) {
            alert.error("No Record Found");
          }
        } else {
          setReport(null);
          alert.error(getErrorMessage(data));
        }
      })
      .catch((err) => {
        setLoading(false);
        setSearched(true);
        setReport(null);
        alert.error("Unable to load home work analysis, please try again");
        console.log(err);
      });
  };

  const cell = { textAlign: "center", verticalAlign: "middle" };
  const startCell = { ...cell, textAlign: "start" };
  const muted = (text) =>
    text === NOT_RECORDED ? (
      <span className="text-muted font-italic">{text}</span>
    ) : (
      text
    );

  const renderSummary = () => (
    <Table responsive bordered ref={setTableElement}>
      <thead>
        <tr>
          <th style={cell}>Sr</th>
          <th style={cell}>Class</th>
          <th style={cell}>Section</th>
          <th style={cell}>Subject</th>
          <th style={cell}>Home Work Uploaded</th>
          <th style={cell}>Days With Home Work</th>
          <th style={cell}>First Date</th>
          <th style={cell}>Last Date</th>
          <th style={cell}>Last Uploaded At</th>
          <th style={cell}>Uploaded By</th>
        </tr>
      </thead>
      <tbody>
        {report.summary.map((row, index) => (
          <tr key={index}>
            <td style={cell}>{index + 1}</td>
            <td style={startCell}>{row.class}</td>
            <td style={startCell}>{row.section}</td>
            <td style={startCell}>{row.subject}</td>
            <td style={{ ...cell, fontWeight: "bold" }}>{row.homework_count}</td>
            <td style={cell}>{row.days_with_homework}</td>
            <td style={cell}>{row.first_date}</td>
            <td style={cell}>{row.last_date}</td>
            <td style={cell}>{row.last_uploaded_at}</td>
            <td style={startCell}>{row.uploaded_by}</td>
          </tr>
        ))}
        <tr style={{ fontWeight: "bold", borderTop: "2px solid black" }}>
          <td style={cell}></td>
          <td style={startCell} colSpan={3}>
            Total
          </td>
          <td style={cell}>{report.totals.homework_count}</td>
          <td style={cell} colSpan={5}></td>
        </tr>
      </tbody>
    </Table>
  );

  const renderUploaders = () => (
    <Table responsive bordered ref={setTableElement}>
      <thead>
        <tr>
          <th style={cell}>Sr</th>
          <th style={cell}>Uploaded By</th>
          <th style={cell}>Home Work Uploaded</th>
          <th style={cell}>Class Sections</th>
          <th style={cell}>Subjects</th>
          <th style={cell}>First Upload</th>
          <th style={cell}>Last Upload</th>
        </tr>
      </thead>
      <tbody>
        {report.uploaders.map((row, index) => (
          <tr key={index}>
            <td style={cell}>{index + 1}</td>
            <td style={startCell}>{muted(row.uploaded_by)}</td>
            <td style={{ ...cell, fontWeight: "bold" }}>{row.homework_count}</td>
            <td style={cell}>{row.class_sections}</td>
            <td style={cell}>{row.subjects}</td>
            <td style={cell}>{row.first_uploaded_at}</td>
            <td style={cell}>{row.last_uploaded_at}</td>
          </tr>
        ))}
        <tr style={{ fontWeight: "bold", borderTop: "2px solid black" }}>
          <td style={cell}></td>
          <td style={startCell}>Total</td>
          <td style={cell}>{report.totals.homework_count}</td>
          <td style={cell} colSpan={4}></td>
        </tr>
      </tbody>
    </Table>
  );

  const renderDetails = () => (
    <Table responsive bordered ref={setTableElement}>
      <thead>
        <tr>
          <th style={cell}>Sr</th>
          <th style={cell}>Date</th>
          <th style={cell}>Class</th>
          <th style={cell}>Section</th>
          <th style={cell}>Subject</th>
          <th style={cell}>Title</th>
          <th style={cell}>Uploaded By</th>
          <th style={cell}>Uploaded At</th>
          <th style={cell}>Last Updated By</th>
          <th style={cell}>Last Updated At</th>
        </tr>
      </thead>
      <tbody>
        {report.details.map((row, index) => (
          <tr key={row.id}>
            <td style={cell}>{index + 1}</td>
            <td style={cell}>{row.date}</td>
            <td style={startCell}>{row.class}</td>
            <td style={startCell}>{row.section}</td>
            <td style={startCell}>{row.subject}</td>
            <td style={startCell}>{row.title}</td>
            <td style={startCell}>{muted(row.uploaded_by)}</td>
            <td style={cell}>{row.uploaded_at}</td>
            <td style={startCell}>{row.last_updated_by ? muted(row.last_updated_by) : "-"}</td>
            <td style={cell}>{row.last_updated_at || "-"}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

  const hasData = report && report.details && report.details.length > 0;
  const viewLabel = VIEWS.find((v) => v.key === view)?.label;

  return (
    <Paper style={{ marginTop: "10px", marginBottom: "10px", padding: "15px" }}>
      <div className="row">
        <div className="col-md-3 pb-4">
          <label className="form-control-label">
            <b>Select Session</b>
          </label>
          <select
            className="form-control form-select h-40px"
            value={sessionId}
            onChange={(e) => setSessionId(e.target.value)}
          >
            <option value="">Select Session</option>
            {allSessions.map((session) => (
              <option key={session.id} value={session.id}>
                {session.year}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-3 pb-4">
          <label className="form-control-label">
            <b>Select Campus</b>
          </label>
          <select
            className="form-control form-select h-40px"
            value={campusId}
            onChange={(e) => onCampusChange(e.target.value)}
          >
            <option value="">Select Campus</option>
            {campusData.map((campus) => (
              <option key={campus.id} value={campus.id}>
                {campus.name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-3 pb-4">
          <label className="form-control-label">
            <b>Select Class (Optional)</b>
          </label>
          <select
            className="form-control form-select h-40px"
            value={classId}
            onChange={(e) => onClassChange(e.target.value)}
            disabled={!campusId}
          >
            <option value="">All Classes</option>
            {classesData.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-3 pb-4">
          <label className="form-control-label">
            <b>Select Education Type</b>
          </label>
          <select
            className="form-control form-select h-40px"
            value={educationType}
            onChange={(e) => onEducationTypeChange(e.target.value)}
          >
            <option value="1">Education</option>
            <option value="2">Hifz</option>
          </select>
        </div>

        <div className="col-md-3 pb-4">
          <label className="form-control-label">
            <b>Select Section (Optional)</b>
          </label>
          <select
            className="form-control form-select h-40px"
            value={sectionId}
            onChange={(e) => setSectionId(e.target.value)}
            disabled={!classId}
          >
            <option value="">All Sections</option>
            {sectionData.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-3 pb-4">
          <label className="form-control-label">
            <b>Select Subject (Optional)</b>
          </label>
          <select
            className="form-control form-select h-40px"
            value={subjectId}
            onChange={(e) => setSubjectId(e.target.value)}
          >
            <option value="">All Subjects</option>
            {subjectsData.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-3 pb-4">
          <label className="form-control-label">
            <b>Select Date (Optional)</b>
          </label>
          <RangePicker className="h-40px w-100" onChange={onDateChange} />
        </div>

        <div className="col-md-3 mt-7">
          <button
            className="btn btn-primary"
            onClick={handleSearch}
            disabled={loading}
          >
            Search
          </button>

          {hasData ? (
            <>
              <ReactToPrint
                documentTitle={`Home Work Analysis - ${viewLabel}`}
                pageStyle={pageStyle}
                removeAfterPrint={true}
                onBeforeGetContent={() => setPrintLoading(true)}
                onAfterPrint={() => setPrintLoading(false)}
                trigger={() => (
                  <button
                    disabled={printLoading}
                    className="btn btn-primary w-105px mx-2"
                  >
                    {printLoading ? <span>Printing..</span> : "Print"}
                  </button>
                )}
                content={() => componentRef.current}
              />
              <DownloadTableExcel
                filename={`Home Work Analysis - ${viewLabel}`}
                sheet="Home Work Analysis"
                currentTableRef={tableElement}
              >
                <button className="btn btn-primary">Export</button>
              </DownloadTableExcel>
            </>
          ) : null}
        </div>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center mt-5">
          <Spinner variant="info" animation="border" />
        </div>
      ) : hasData ? (
        <div className="mt-3">
          <div className="d-flex flex-wrap mb-4">
            {[
              ["Home Work Uploaded", report.totals.homework_count],
              ["Class Sections", report.totals.class_sections],
              ["Subjects", report.totals.subjects],
              ["Uploaders", report.totals.uploaders],
            ].map(([label, value]) => (
              <div
                key={label}
                className="border rounded px-5 py-3 mr-4 mb-2 text-center"
              >
                <div className="text-muted">{label}</div>
                <div style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
                  {value}
                </div>
              </div>
            ))}
          </div>

          <div className="btn-group mb-4" role="group">
            {VIEWS.map((v) => (
              <button
                key={v.key}
                type="button"
                className={`btn ${
                  view === v.key ? "btn-primary" : "btn-light-primary"
                }`}
                onClick={() => setView(v.key)}
              >
                {v.label}
              </button>
            ))}
          </div>

          <div ref={componentRef}>
            <div className="row p-3">
              <img style={{ width: 100, height: 100 }} src={icon} alt="logo" />
              <div className="col-md-10" style={{ lineHeight: 1.5 }}>
                <h1>{unitName}</h1>
                <h4>{reportTitle}</h4>
                <h5 className="text-muted">{viewLabel}</h5>
              </div>
            </div>

            {view === "summary" && renderSummary()}
            {view === "uploaders" && renderUploaders()}
            {view === "details" && renderDetails()}
          </div>

          {report.totals.not_recorded > 0 && (
            <p className="text-muted mt-3" style={{ fontSize: "12px" }}>
              {report.totals.not_recorded} home work were uploaded before the
              uploader started being recorded, so their uploader shows as "
              {NOT_RECORDED}".
            </p>
          )}
        </div>
      ) : searched ? (
        <div className="text-center p-5">
          <h4 className="text-muted">
            No home work found for the selected criteria
          </h4>
        </div>
      ) : null}
    </Paper>
  );
};

export default HomeWorkAnalysis;
